import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Trash2, Archive, RotateCcw, Star, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

export type FeedbackStatus = "pending" | "approved" | "archived";

export interface FeedbackEntry {
  id: string;
  name: string | null;
  rating: number;
  message: string | null;
  status: FeedbackStatus;
  is_published?: boolean;
  created_at: string;
}

export default function AdminFeedbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const fetchFeedback = useCallback(
    async (retryCount = 0) => {
      const MAX_RETRIES = 3;
      const RETRY_DELAY_MS = 2000;
      const isSchemaCacheError = (m: string) =>
        /schema cache|could not find the table|does not exist/i.test(m);

      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from("feedback")
          .select("id, name, rating, message, status, is_published, created_at")
          .order("created_at", { ascending: false });

        if (fetchError) throw fetchError;
        setEntries((data ?? []) as FeedbackEntry[]);
      } catch (err) {
        const msg =
          (err as { message?: string })?.message ?? (err instanceof Error ? err.message : null) ?? String(err);
        if (isSchemaCacheError(msg) && retryCount < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          return fetchFeedback(retryCount + 1);
        }
        setError(msg);
        toast.error(t("admin.feedback.error"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const updateStatus = useCallback(
    async (id: string, status: FeedbackStatus) => {
      setActingId(id);
      const prev = entries.find((e) => e.id === id);
      if (!prev) return;

      setEntries((list) =>
        list.map((e) => (e.id === id ? { ...e, status } : e))
      );

      const payload =
        status === "approved"
          ? { status: "approved" as const, is_published: true }
          : { status, is_published: false };

      try {
        const { error: updateError } = await supabase
          .from("feedback")
          .update(payload)
          .eq("id", id);

        if (updateError) throw updateError;

        const key =
          status === "approved"
            ? "admin.feedback.approveSuccess"
            : status === "archived"
              ? "admin.feedback.archiveSuccess"
              : "admin.feedback.restoreSuccess";
        toast.success(t(key));
        fetchFeedback();
      } catch {
        setEntries((list) =>
          list.map((e) => (e.id === id ? { ...e, status: prev.status } : e))
        );
        const key =
          status === "approved"
            ? "admin.feedback.approveError"
            : status === "archived"
              ? "admin.feedback.archiveError"
              : "admin.feedback.restoreError";
        toast.error(t(key));
      } finally {
        setActingId(null);
      }
    },
    [entries, t, fetchFeedback]
  );

  const deleteEntry = useCallback(
    async (id: string) => {
      setActingId(id);
      const prev = entries.find((e) => e.id === id);
      if (!prev) return;

      setEntries((list) => list.filter((e) => e.id !== id));

      try {
        const { error: deleteError } = await supabase
          .from("feedback")
          .delete()
          .eq("id", id);

        if (deleteError) throw deleteError;
        toast.success(t("admin.feedback.deleteSuccess"));
        fetchFeedback();
      } catch {
        setEntries((list) =>
          [...list, prev].sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )
        );
        toast.error(t("admin.feedback.deleteError"));
      } finally {
        setActingId(null);
      }
    },
    [entries, t, fetchFeedback]
  );

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderStars = (rating: number) => {
    const arr: React.ReactNode[] = [];
    for (let i = 1; i <= 5; i++) {
      arr.push(
        <Star
          key={i}
          size={14}
          className={
            i <= rating
              ? "fill-amber-400 text-amber-400"
              : "text-zinc-500"
          }
        />
      );
    }
    return <span className="inline-flex items-center gap-0.5">{arr}</span>;
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="mx-auto max-w-6xl text-zinc-100">
        <div className="mb-6 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-800/80 px-4 py-2 text-sm font-medium text-zinc-200 transition-colors hover:bg-zinc-700/80"
            aria-label={t("admin.feedback.backToHome")}
          >
            <ArrowLeft size={18} className="shrink-0" />
            {t("admin.feedback.backToHome")}
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-100">
              {t("admin.feedback.title")}
            </h1>
            <p className="text-sm text-zinc-400">
              {t("admin.feedback.subtitle")}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-zinc-700/60 bg-zinc-900/80 py-16">
            <Loader2 size={32} className="animate-spin text-purple-400" />
            <p className="text-sm text-zinc-400">
              {t("admin.feedback.loading")}
            </p>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-8 text-center">
            <p className="text-red-400">{error}</p>
            <button
              type="button"
              onClick={fetchFeedback}
              className="mt-4 rounded-xl bg-red-500/20 px-4 py-2 text-sm font-medium text-red-400 hover:bg-red-500/30"
            >
              {t("buttons.retry")}
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-zinc-700/60 bg-zinc-900/80 py-16">
            <p className="font-medium text-zinc-200">
              {t("admin.feedback.empty")}
            </p>
            <p className="text-sm text-zinc-400">
              {t("admin.feedback.emptyHint")}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-zinc-700/60 bg-zinc-900/80">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-700/80 bg-zinc-800/60">
                    <th className="px-4 py-3 font-semibold text-zinc-200">
                      {t("admin.feedback.name")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-zinc-200">
                      {t("admin.feedback.rating")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-zinc-200">
                      {t("admin.feedback.comment")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-zinc-200">
                      {t("admin.feedback.timestamp")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-zinc-200">
                      {t("admin.feedback.status")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-zinc-200">
                      {t("admin.feedback.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-zinc-700/40 transition-colors hover:bg-zinc-800/50 last:border-0"
                    >
                      <td className="px-4 py-3 text-zinc-200">
                        {entry.name || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {renderStars(entry.rating)}
                      </td>
                      <td className="max-w-[280px] truncate px-4 py-3 text-zinc-400">
                        {entry.message || "—"}
                      </td>
                      <td className="px-4 py-3 text-zinc-400">
                        {formatDate(entry.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-lg px-2 py-1 text-xs font-medium ${
                            entry.status === "approved"
                              ? "bg-emerald-500/20 text-emerald-600"
                              : entry.status === "archived"
                                ? "bg-zinc-500/20 text-zinc-400"
                                : "bg-amber-500/20 text-amber-400"
                          }`}
                        >
                          {t(`admin.feedback.status${entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {entry.status !== "approved" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateStatus(entry.id, "approved")
                                  }
                                  disabled={!!actingId}
                                  className="rounded-lg p-2 text-emerald-600 transition-colors hover:bg-emerald-500/20 disabled:opacity-50"
                                  aria-label={t("admin.feedback.approve")}
                                >
                                  {actingId === entry.id ? (
                                    <Loader2 size={16} className="animate-spin" />
                                  ) : (
                                    <Check size={16} />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t("admin.feedback.approve")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {entry.status !== "archived" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateStatus(entry.id, "archived")
                                  }
                                  disabled={!!actingId}
                                  className="rounded-lg p-2 text-zinc-400 transition-colors hover:bg-zinc-500/20 hover:text-zinc-300 disabled:opacity-50"
                                  aria-label={t("admin.feedback.archive")}
                                >
                                  {actingId === entry.id ? (
                                    <Loader2 size={16} className="animate-spin" />
                                  ) : (
                                    <Archive size={16} />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t("admin.feedback.archive")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {entry.status === "archived" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateStatus(entry.id, "pending")
                                  }
                                  disabled={!!actingId}
                                  className="rounded-lg p-2 text-amber-400 transition-colors hover:bg-amber-500/20 disabled:opacity-50"
                                  aria-label={t("admin.feedback.restore")}
                                >
                                  {actingId === entry.id ? (
                                    <Loader2 size={16} className="animate-spin" />
                                  ) : (
                                    <RotateCcw size={16} />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t("admin.feedback.restore")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => deleteEntry(entry.id)}
                                disabled={!!actingId}
                                className="rounded-lg p-2 text-red-400 transition-colors hover:bg-red-500/20 disabled:opacity-50"
                                aria-label={t("admin.feedback.delete")}
                              >
                                {actingId === entry.id ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("admin.feedback.delete")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
